import React, { useEffect, useRef, useState } from 'react';
import { UserX, BookOpen, RefreshCw, Users, CheckCircle, ArrowRight, GraduationCap } from 'lucide-react';
import { retrainSectionData } from '../../data/mock';

const iconMap = {
  UserX,
  BookOpen,
  RefreshCw,
  Users
};

const RetrainSection = ({ onOpenCalendar }) => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-20 md:py-28 relative overflow-hidden"
      style={{ background: 'var(--bg-page)' }}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-grid opacity-30" />
      
      <div className="container relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div 
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
              isVisible ? 'animate-fadeInUp' : 'opacity-0'
            }`}
            style={{ background: 'var(--brand-primary)', color: 'var(--brand-dark)' }}
          >
            <GraduationCap size={16} />
            <span className="font-semibold text-sm">{retrainSectionData.badge}</span>
          </div>
          
          <h2 
            className={`heading-1 mb-4 ${isVisible ? 'animate-fadeInUp stagger-1' : 'opacity-0'}`}
            style={{ color: 'var(--text-primary)' }}
          >
            {retrainSectionData.title}
          </h2>
          <p 
            className={`display-small mb-4 ${isVisible ? 'animate-fadeInUp stagger-2' : 'opacity-0'}`}
            style={{ color: 'var(--brand-dark)' }}
          >
            {retrainSectionData.subtitle}
          </p>
          <p 
            className={`body-large ${isVisible ? 'animate-fadeInUp stagger-3' : 'opacity-0'}`}
            style={{ color: 'var(--text-secondary)' }}
          >
            {retrainSectionData.description}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-12 max-w-5xl mx-auto">
          {retrainSectionData.features.map((feature, index) => {
            const Icon = iconMap[feature.icon];
            return (
              <div 
                key={index}
                className={`card p-6 group ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}
                style={{ animationDelay: `${(index + 4) * 100}ms`, background: 'white' }}
              >
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
                  style={{ background: 'var(--brand-primary)' }}
                >
                  <Icon size={28} style={{ color: 'var(--brand-dark)' }} />
                </div>
                <h3 className="heading-3 mb-2" style={{ color: 'var(--text-primary)' }}>
                  {feature.title}
                </h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Outcomes Section */}
        <div 
          className={`max-w-4xl mx-auto rounded-3xl p-8 md:p-10 ${isVisible ? 'animate-scaleIn' : 'opacity-0'}`}
          style={{ background: 'var(--brand-dark)', animationDelay: '600ms' }}
        >
          <h3 className="heading-2 text-center mb-8" style={{ color: 'white' }}>
            What Your Team Achieves
          </h3>
          
          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            {retrainSectionData.outcomes.map((outcome, index) => (
              <div 
                key={index}
                className="flex items-start gap-3 p-4 rounded-xl"
                style={{ background: 'rgba(255, 255, 255, 0.1)' }}
              >
                <CheckCircle 
                  size={20} 
                  className="flex-shrink-0 mt-0.5" 
                  style={{ color: 'var(--brand-primary)' }} 
                />
                <span className="body-medium" style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  {outcome}
                </span>
              </div>
            ))}
          </div>

          <div className="text-center">
            <button
              onClick={onOpenCalendar}
              className="btn-accent"
              data-testid="retrain-section-cta"
            >
              {retrainSectionData.cta}
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RetrainSection;
